import React, { useEffect, useState, } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Checkbox,
    Button, TextField, MenuItem, FormControl, InputLabel, Select, FormControlLabel,
} from '@mui/material';

import type {
    OrganisationTypeDto,
    OrganisationListItemDto,
    OrganisationUpsertDto
} from "../models/Organisation";

import useAuthToken from '../functions/useAuthToken';
import { useOrganisationsApi } from "../functions/OrganisationsApi";

import "./OrganisationsAdminStyleSheet.css";

const OrganisationDialog = ({
    open,
    organisation,
    types,
    onClose,
    onSaved
}: {
    open: boolean;
    organisation: OrganisationListItemDto | null;
    types: OrganisationTypeDto[];
    onClose: () => void;
    onSaved: () => void;
    }) => {

    const getAccessToken = useAuthToken();

    const {
        getTypes,
        list,
        create,
        update,
        deleteOrganisation
    } = useOrganisationsApi();

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [organisationTypeId, setOrganisationTypeId] = useState<number>(1);
    const [isSystemOrg, setIsSystemOrg] = useState(false);

    useEffect(() => {
        if (organisation) {
            setName(organisation.name);
            setDescription(organisation.description ?? "");
            setOrganisationTypeId(organisation.organisationTypeId);
            setIsSystemOrg(organisation.isSystemOrg);
        } else {
            setName("");
            setDescription("");
            setOrganisationTypeId(1);
            setIsSystemOrg(false);
        }
    }, [organisation, open]);

    const save = async () => {
        const dto: OrganisationUpsertDto = {
            name,
            description,
            organisationTypeId,
            isSystemOrg
        };

        const accessToken = await getAccessToken();
        if (!accessToken) return;

        if (organisation) {

            await update(organisation.id, dto, accessToken);
        } else {
            await create(dto, accessToken);
        }

        onSaved();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {organisation ? "Edit Organisation" : "Add Organisation"}
            </DialogTitle>

            <DialogContent className="dialog-form">
                <TextField
                    label="Name"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    margin="normal"
                />

                <FormControl fullWidth margin="normal">
                    <InputLabel>Organisation Type</InputLabel>
                    <Select
                        label="Organisation Type"
                        value={organisationTypeId}
                        onChange={(e) => setOrganisationTypeId(Number(e.target.value))}
                    >
                        {types.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                                {t.type}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="Description"
                    fullWidth
                    multiline
                    minRows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    margin="normal"
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isSystemOrg}
                            onChange={(e) => setIsSystemOrg(e.target.checked)}
                        />
                    }
                    label="System organisation"
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={!name.trim()}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrganisationDialog;